<script setup lang="ts">
import type { MegamenuNavigationItems } from '~/data/megamenu'
import type { INavLinkItem } from './NavigationLinks.vue'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { useSearchEngine } from '~/composables/search/useSearchEngine'
import { useHomepageBannerSearchVisibility } from '~/composables/search/useHomepageBannerSearchVisibility'
import { useFocusWithin } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    logoImg?: string
    extraLinkItems?: INavLinkItem[]
  }>(),
  { logoImg: '/_nuxt3-static/logos/logo.svg', extraLinkItems: () => [] }
)

const $localePath = useLocalePath()
const localePath = $localePath
const isSubdomain = useIsSubdomain()
const route = useRoute()
const { t, tm } = useI18n()
const megamenuNavItems = computed<MegamenuNavigationItems>(() => {
  const items = tm('megamenu.navigationItems') as
    | MegamenuNavigationItems
    | undefined
  return Array.isArray(items) ? items : []
})
const isNavSidebarOpen = ref(false)

const { isMobile } = useDevice()
// Navbar search engine (KFT-4035): Typesense vs Algolia autocomplete, per the
// shared useSearchEngine() state (KFT-4115) — it can switch after hydration
// when an A/B tool forces the other engine, swapping the component live.
const searchEngine = useSearchEngine()
const toggleNavSidebar = () => {
  isNavSidebarOpen.value = !isNavSidebarOpen.value
  document.body.classList.toggle('prevent-scroll')
}

const closeNavSidebar = () => {
  isNavSidebarOpen.value = false
  document.body.classList.remove('prevent-scroll')
}

const isMounted = ref(false)
const isSearchLoaded = ref(false)

onMounted(() => {
  isMounted.value = true
  nextTick(() => {
    setTimeout(() => {
      isSearchLoaded.value = true
    }, 100)
  })
})

// Reset state on route change to handle CLS properly
watch(
  () => route.fullPath,
  () => {
    isSearchLoaded.value = false
    // Small delay to ensure DOM is ready for placeholder
    nextTick(() => {
      setTimeout(() => {
        isSearchLoaded.value = true
      }, 100)
    })
  },
  { immediate: false }
)

const defaultLinks = computed<INavLinkItem[]>(() => {
  const items = megamenuNavItems.value
  const links: INavLinkItem[] = items
    .filter((item) => !isSubdomain.value || item.showOnSub)
    .map((item) => ({
      path: item.path,
      text: item.label,
      ...(item.showOnSub && { showOnSub: true }),
    }))
  if (isSubdomain.value) {
    links.unshift({ path: '/', text: t('general.allEvents'), showOnSub: true })
  }
  if (props.extraLinkItems?.length) {
    links.push(...props.extraLinkItems)
  }
  return links
})

const navLinks = computed(() => defaultLinks.value)

const megamenuDataForNav = computed(() =>
  isSubdomain.value ? undefined : megamenuNavItems.value
)

const routesWhereSearchInNavbarIsHidden = ['index___de-de', 'index___en-us']
const isHomepage = computed(() =>
  routesWhereSearchInNavbarIsHidden.includes(route.name as string)
)

// Sticky search on the homepage (KFT-4035 item 3): the navbar search is hidden
// while the banner search is on screen and revealed once it scrolls under the
// sticky header. It mounts on the first reveal and then only toggles v-show, so
// a typed query survives and the search chunk loads only if the user scrolls.
const isBannerSearchVisible = useHomepageBannerSearchVisibility()
const isStickySearchRevealed = computed(
  () => isHomepage.value && !isBannerSearchVisible.value
)
const hasRevealedStickySearch = ref(false)
watch(isStickySearchRevealed, (revealed) => {
  if (revealed) hasRevealedStickySearch.value = true
})
// Don't hide the desktop search under someone typing in it when they scroll
// back up.
const desktopSearchRef = ref<HTMLElement | null>(null)
const { focused: isDesktopSearchFocused } = useFocusWithin(desktopSearchRef)
const isSearchShown = computed(
  () =>
    !isHomepage.value ||
    isStickySearchRevealed.value ||
    isDesktopSearchFocused.value
)

const shouldShowFederatedSearchInCurrentRoute = computed(() => {
  const routeName = route.name as string | undefined
  return (
    routeName &&
    (!isHomepage.value || hasRevealedStickySearch.value) &&
    !isSubdomain.value &&
    isMounted.value
  )
})
</script>

<template>
  <div>
    <nav class="relative bg-white border-b sm:border-b-0 border-gray-200">
      <div
        class="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-4 h-[70px]"
      >
        <HamburgerButton @navbar-open="toggleNavSidebar" />
        <NuxtLink :to="localePath('/')" class="app-logo">
          <img
            :src="logoImg"
            class="h-full w-full object-contain"
            alt="konfetti logo"
          />
        </NuxtLink>
        <!-- Federated search and it's placeholder in desktop -->
        <div
          ref="desktopSearchRef"
          class="hidden sm:block grow flex justify-center md:w-[calc(100%-720px)] h-[38px] mx-4"
        >
          <!--          <FederatedSearchLoadingPlaceholder-->
          <!--            v-if="-->
          <!--              !isSearchLoaded &&-->
          <!--              !isSubdomain &&-->
          <!--              route.name &&-->
          <!--              !routesWhereSearchInNavbarIsHidden.includes(route.name as string)-->
          <!--            "-->
          <!--            class="fullwidth hidden sm:flex"-->
          <!--          />-->

          <!--
            The Typesense root takes no id (elementId only feeds the input id),
            so the #navbar-autocomplete-* scoped rule doesn't reach it — apply
            its grow/mx-4 spacing via class instead.
          -->
          <!-- Fade only for the homepage sticky reveal; elsewhere it's instant. -->
          <Transition
            :css="isHomepage"
            enter-active-class="transition-opacity duration-200"
            enter-from-class="opacity-0"
            leave-active-class="transition-opacity duration-150"
            leave-to-class="opacity-0"
          >
            <FederatedSearchTypesense
              v-if="
                searchEngine === 'typesense' &&
                shouldShowFederatedSearchInCurrentRoute &&
                isMounted &&
                !isMobile
              "
              v-show="isSearchShown"
              element-id="navbar-autocomplete-desktop"
              analytics-placement="navbar"
              :full-width="true"
              class="hidden sm:block grow mx-4"
            />
            <LazyFederatedSearch
              v-else-if="
                shouldShowFederatedSearchInCurrentRoute &&
                isMounted &&
                !isMobile
              "
              v-show="isSearchShown"
              element-id="navbar-autocomplete-desktop"
              analytics-placement="navbar"
              :full-width="true"
              class="hidden sm:block"
            />
          </Transition>
        </div>
        <NuxtLazyHydrate when-idle>
          <NavbarUserMenu />
        </NuxtLazyHydrate>
      </div>
      <!--
        Federated search and placeholder in mobile (viewport: hidden on sm+).
        On the homepage it is the sticky reveal (KFT-4035 item 3): the row is
        absolutely positioned under the nav, overlaying the page, so revealing
        it doesn't grow the sticky header and push content down.
      -->
      <Transition
        :css="isHomepage"
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="-translate-y-2 opacity-0"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="-translate-y-2 opacity-0"
      >
        <div
          v-if="
            !isSubdomain &&
            route.name &&
            (!isHomepage || hasRevealedStickySearch)
          "
          v-show="isSearchShown"
          class="px-1 pb-2 block sm:hidden"
          :class="
            isHomepage
              ? 'absolute inset-x-0 top-full h-[52px] bg-white border-b border-gray-200'
              : 'relative h-11'
          "
        >
          <!--
          Match the mounted box: #navbar-autocomplete-mobile gets a 1rem (16px)
          side margin from the scoped rule below, plus the parent px-1 (4px) =
          20px inset. The placeholder has no id, so inset it explicitly to avoid
          an edge-to-edge → inset shift on hydration.
        -->
          <!--
          Typesense renders its real trigger button immediately, so it needs no
          loading placeholder (that is an Algolia-era CLS device).
        -->
          <FederatedSearchTypesense
            v-if="
              searchEngine === 'typesense' &&
              shouldShowFederatedSearchInCurrentRoute &&
              isMobile
            "
            element-id="navbar-autocomplete-mobile"
            analytics-placement="navbar"
            :full-width="true"
            class="block sm:hidden px-4"
          />
          <template v-else-if="searchEngine !== 'typesense'">
            <FederatedSearchLoadingPlaceholder
              v-show="!isSearchLoaded"
              class="fullwidth block sm:hidden absolute left-5 right-5"
            />
            <FederatedSearch
              v-if="shouldShowFederatedSearchInCurrentRoute && isMobile"
              element-id="navbar-autocomplete-mobile"
              analytics-placement="navbar"
              :full-width="true"
              class="fullwidth block sm:hidden border-none"
            />
          </template>
        </div>
      </Transition>
    </nav>
  </div>
  <NuxtLazyHydrate when-idle>
    <NavigationLinks
      :is-open="isNavSidebarOpen"
      :nav-links="navLinks"
      :megamenu-data="megamenuDataForNav"
      @navbar-close="closeNavSidebar"
    />
  </NuxtLazyHydrate>
  <NuxtLazyHydrate when-idle>
    <CartSidebar />
  </NuxtLazyHydrate>
</template>
<style scoped lang="postcss">
#navbar-autocomplete-desktop,
#navbar-autocomplete-mobile {
  @apply grow;
  @apply mx-4;
}
</style>
<style>
body.prevent-scroll {
  overflow: hidden;
}
.app-logo {
  @apply w-[142px] h-[24px];
  @apply flex items-center space-x-3 rtl:space-x-reverse;
  @media (min-width: 372px) {
    @apply ml-2;
  }
  @media (min-width: 400px) {
    @apply ml-4;
  }
  @media (min-width: 768px) {
    @apply ml-0;
  }
}
</style>
